import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import {
  INSTINCTS_HELP, RULES_HELP, SOURCE_KINDS, TRUST_LABEL, errorMessage, move, orNull, weightsLine, type Trust,
} from "@/features/think-tank/ttModel";

type TtRulesView = components["schemas"]["TtRulesView"];
type TtSource = components["schemas"]["TtSourcePublic"];
type Instinct = { label: string; opportunity_id: string | null };

const KEY = ["admin", "think-tank"] as const;

/** The rules Randy and Jeff own: weights, his instincts, guardrails and sources. Versioned. */
export function Rules() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: [...KEY, "rules"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/rules");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [...KEY] });
  const r = query.data;
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (query.isError || !r) {
    return (
      <Card><CardBody>
        <p role="alert" className="text-sm text-red-700">Couldn’t load the rules. Your session may have expired.</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button>
      </CardBody></Card>
    );
  }
  return (
    <div className="space-y-4">
      <Editor key={r.current.version} r={r} onChanged={refresh} />
      <Versions r={r} />
      <Sources sources={r.sources} line={r.trust_line} onChanged={refresh} />
    </div>
  );
}

function Editor({ r, onChanged }: { r: TtRulesView; onChanged: () => void }) {
  const cur = r.current;
  const [weights, setWeights] = useState<Record<string, number>>(Object.fromEntries(cur.weights.map(w => [w.rule, w.weight])));
  const [instincts, setInstincts] = useState<Instinct[]>(cur.instincts.map(i => ({ label: i.label, opportunity_id: i.opportunity_id ?? null })));
  const [guardrails, setGuardrails] = useState<string[]>(cur.guardrails);
  const [note, setNote] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const [newInstinct, setNewInstinct] = useState("");
  const [newGuardrail, setNewGuardrail] = useState("");
  const w = weightsLine(weights);
  const dirty = JSON.stringify([weights, instincts, guardrails])
    !== JSON.stringify([Object.fromEntries(cur.weights.map(x => [x.rule, x.weight])), cur.instincts.map(i => ({ label: i.label, opportunity_id: i.opportunity_id ?? null })), cur.guardrails]);
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/rules", {
        body: { weights, instincts, guardrails, note: orNull(note) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t save the rules."));
    },
    onSuccess: () => { setNote(""); onChanged(); },
  });
  return (
    <>
      <Card data-testid="tt-weights"><CardBody>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-semibold">What matters, and how much</h2>
          <span className="text-xs text-zinc-500" data-testid="tt-rules-line">{cur.line}</span>
        </div>
        <div className="mt-3 space-y-3">
          {cur.weights.map(x => (
            <div key={x.rule} className="grid grid-cols-[1fr_auto] items-center gap-x-3 sm:grid-cols-[14rem_1fr_3rem]">
              <label htmlFor={`tt-w-${x.rule}`} className="text-sm">{x.label}</label>
              <span className="text-right text-sm font-semibold sm:order-last">{weights[x.rule]}%</span>
              <input id={`tt-w-${x.rule}`} type="range" min={0} max={50} step={5} value={weights[x.rule]} data-testid={`tt-w-${x.rule}`}
                onChange={e => setWeights({ ...weights, [x.rule]: Number(e.target.value) })} className="col-span-2 w-full accent-zinc-900 sm:col-span-1" />
            </div>
          ))}
        </div>
        <p className={`mt-2 text-sm ${w.ok ? "text-zinc-600" : "text-red-700"}`} data-testid="tt-weights-line">{w.line}</p>
        <p className="mt-1 text-xs text-zinc-500">{RULES_HELP}</p>
      </CardBody></Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="tt-instincts"><CardBody>
          <h2 className="font-semibold">Randy’s instincts</h2>
          <p className="text-sm text-zinc-500">{INSTINCTS_HELP}</p>
          <ol className="mt-2 space-y-2">
            {instincts.map((i, n) => (
              <li key={`${n}:${i.label}`} className="space-y-1 text-sm" data-testid={`tt-instinct-${n}`}>
                <div className="flex flex-wrap items-center gap-x-2">
                  <span className="w-5 shrink-0 font-semibold text-zinc-400">{n + 1}</span>
                  {/* The name gets its own line on a phone; the controls sit beside it from 640 px. */}
                  <span className="min-w-0 flex-1 basis-[calc(100%-1.75rem)] font-medium sm:basis-0" data-testid={`tt-instinct-name-${n}`}>{i.label}</span>
                  <div className="ml-7 flex items-center sm:ml-0">
                    <Button size="sm" variant="ghost" className="px-2" aria-label={`Move ${i.label} up`} disabled={n === 0} onClick={() => setInstincts(move(instincts, n, -1))}>↑</Button>
                    <Button size="sm" variant="ghost" className="px-2" aria-label={`Move ${i.label} down`} disabled={n === instincts.length - 1} onClick={() => setInstincts(move(instincts, n, 1))}>↓</Button>
                    {removing === `i${n}` ? null : <Button size="sm" variant="ghost" className="px-2" data-testid={`tt-instinct-remove-${n}`} onClick={() => setRemoving(`i${n}`)}>Take off</Button>}
                  </div>
                </div>
                <div className="pl-7" data-testid={`tt-instinct-link-${n}`}>
                  <select aria-label={`What “${i.label}” means on the scorecard`} value={i.opportunity_id ?? ""}
                    onChange={e => setInstincts(instincts.map((x, k) => k === n ? { ...x, opportunity_id: e.target.value || null } : x))}
                    className="h-8 w-full rounded-md border border-zinc-300 px-1 text-xs">
                    <option value="">Not on the scorecard</option>
                    {r.opportunities.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                </div>
                {removing === `i${n}` ? (
                  <div role="group" aria-label={`Confirm taking ${i.label} off`} className="flex flex-wrap items-center gap-1 pl-7">
                    <span className="text-xs">Take it off the list?</span>
                    <Button size="sm" variant="danger" data-testid={`tt-instinct-remove-yes-${n}`} onClick={() => { setInstincts(instincts.filter((_, k) => k !== n)); setRemoving(null); }}>Take off</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>Keep</Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
          <div className="mt-2 flex gap-2">
            <input aria-label="Another instinct" placeholder="Another instinct" value={newInstinct} onChange={e => setNewInstinct(e.target.value)}
              className="h-8 flex-1 rounded-md border border-zinc-300 px-2 text-sm" />
            <Button size="sm" variant="secondary" disabled={!newInstinct.trim()} onClick={() => { setInstincts([...instincts, { label: newInstinct.trim(), opportunity_id: null }]); setNewInstinct(""); }}>Add</Button>
          </div>
        </CardBody></Card>

        <Card data-testid="tt-guardrails"><CardBody>
          <h2 className="font-semibold">Guardrails</h2>
          <p className="text-sm text-zinc-500">Lines no move crosses. The report and pilots are checked against them.</p>
          <ul className="mt-2 space-y-2">
            {guardrails.map((g, n) => (
              <li key={n} className="flex flex-wrap items-center gap-2 text-sm">
                <input aria-label={`Guardrail ${n + 1}`} value={g} onChange={e => setGuardrails(guardrails.map((x, k) => k === n ? e.target.value : x))}
                  className="h-8 min-w-0 flex-1 rounded-md border border-zinc-300 px-2" />
                {removing === `g${n}` ? (
                  <span role="group" aria-label="Confirm taking the guardrail off" className="flex items-center gap-1">
                    <span className="text-xs">Take it off?</span>
                    <Button size="sm" variant="danger" data-testid={`tt-guardrail-remove-yes-${n}`} onClick={() => { setGuardrails(guardrails.filter((_, k) => k !== n)); setRemoving(null); }}>Take off</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>Keep</Button>
                  </span>
                ) : <Button size="sm" variant="ghost" data-testid={`tt-guardrail-remove-${n}`} onClick={() => setRemoving(`g${n}`)}>Take off</Button>}
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2">
            <input aria-label="Another guardrail" placeholder="Another guardrail" value={newGuardrail} onChange={e => setNewGuardrail(e.target.value)}
              className="h-8 flex-1 rounded-md border border-zinc-300 px-2 text-sm" />
            <Button size="sm" variant="secondary" disabled={!newGuardrail.trim()} onClick={() => { setGuardrails([...guardrails, newGuardrail.trim()]); setNewGuardrail(""); }}>Add</Button>
          </div>
        </CardBody></Card>
      </div>

      <Card><CardBody>
        <div className="flex flex-wrap items-center gap-2">
          <input aria-label="What changed" placeholder="What changed and why (optional)" value={note} onChange={e => setNote(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-md border border-zinc-300 px-2 text-sm" />
          <Button disabled={!dirty || !w.ok || save.isPending} data-testid="tt-rules-save" onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : `Save as v${cur.version + 1}`}
          </Button>
        </div>
        <p className="mt-1 text-xs text-zinc-500">{dirty ? "Saving keeps v" + cur.version + " too, so nothing is lost." : "No changes yet."}</p>
        {save.isError ? <p role="alert" className="mt-1 text-sm text-red-700">{save.error.message}</p> : null}
      </CardBody></Card>
    </>
  );
}

function Versions({ r }: { r: TtRulesView }) {
  const [version, setVersion] = useState<number | null>(null);
  const compare = useQuery({
    queryKey: [...KEY, "compare", version],
    enabled: version !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/rules/compare", { params: { query: { version: version! } } });
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const c = version !== null ? compare.data : undefined;
  return (
    <Card data-testid="tt-versions"><CardBody>
      <h2 className="font-semibold">Versions · what the ranking would have been</h2>
      <ul className="mt-2 divide-y divide-zinc-100 text-sm">
        {r.versions.map(v => (
          <li key={v.version} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="font-medium">{v.line}</span>
              {v.note ? <span className="block text-xs text-zinc-500">{v.note}</span> : null}
              <span className="block text-xs text-zinc-500">{v.weights.map(x => `${x.short} ${x.weight}%`).join(" · ")}</span>
            </div>
            {v.version !== r.current.version ? (
              <Button size="sm" variant="secondary" data-testid={`tt-compare-${v.version}`} onClick={() => setVersion(v.version)}>See the ranking then</Button>
            ) : <span className="text-xs text-zinc-500">Current</span>}
          </li>
        ))}
      </ul>
      {c ? (
        <div className="mt-3 rounded-md border border-zinc-200 p-3" data-testid="tt-compare">
          <p className="text-sm font-medium">{c.line}</p>
          <div className="mt-2 grid gap-4 sm:grid-cols-2">
            {([["Under v" + c.version, c.then], ["Now (v" + c.current + ")", c.now]] as const).map(([label, list]) => (
              <div key={label}>
                <h3 className="text-xs font-semibold uppercase text-zinc-500">{label}</h3>
                <ol className="mt-1 space-y-1 text-sm">
                  {list.map(x => <li key={x.id}>{x.rank}. {x.title} <span className="text-zinc-500">· {x.score}</span></li>)}
                </ol>
              </div>
            ))}
          </div>
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setVersion(null)}>Close</Button>
        </div>
      ) : null}
    </CardBody></Card>
  );
}

function Sources({ sources, line, onChanged }: { sources: TtSource[]; line: string; onChanged: () => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("other");
  const [trust, setTrust] = useState<Trust>("trusted");
  const update = useMutation({
    mutationFn: async (v: { id: string; trust?: Trust; weight?: number; note?: string | null }) => {
      const { id, ...body } = v;
      const { error } = await api.PATCH("/api/v1/admin/think-tank/sources/{source_id}", { params: { path: { source_id: id } }, body });
      if (error) throw new Error(errorMessage(error, "Couldn’t save that source."));
    },
    onSuccess: onChanged,
  });
  const add = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/sources", { body: { name: name.trim(), kind: kind as TtSource["kind"], trust } });
      if (error) throw new Error(errorMessage(error, "Couldn’t add that source."));
    },
    onSuccess: () => { setName(""); onChanged(); },
  });
  return (
    <Card data-testid="tt-sources"><CardBody>
      <h2 className="font-semibold">Where to look</h2>
      <p className="text-sm text-zinc-500">{line}</p>
      {(["trusted", "weak", "blocked"] as const).map(t => {
        const list = sources.filter(s => s.trust === t);
        return (
          <div key={t} className="mt-3">
            <h3 className="text-xs font-semibold uppercase text-zinc-500">{`${TRUST_LABEL[t]}${list.length ? "" : ": none yet"}`}</h3>
            <ul className="divide-y divide-zinc-100">
              {list.map(s => (
                <li key={s.id} className="flex flex-col gap-1 py-2 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid={`tt-source-${s.id}`}>
                  <div className="min-w-0">
                    <span className="font-medium">{s.name}</span>
                    <span className="block text-xs text-zinc-500">{s.line}{s.cited ? ` · cited ${s.cited}×` : ""}</span>
                  </div>
                  <select aria-label={`How much to trust ${s.name}`} value={s.trust} disabled={update.isPending}
                    onChange={e => update.mutate({ id: s.id, trust: e.target.value as Trust })}
                    className="h-8 rounded-md border border-zinc-300 px-2 text-sm" data-testid={`tt-trust-${s.id}`}>
                    <option value="trusted">Trusted</option>
                    <option value="weak">Weak (counts half)</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <div className="mt-3 grid gap-2 border-t border-zinc-100 pt-3 sm:grid-cols-[1fr_auto_auto_auto]" role="group" aria-label="Add a source">
        <input aria-label="Source name" placeholder="Source name" value={name} onChange={e => setName(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
        <select aria-label="Kind of source" value={kind} onChange={e => setKind(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          {SOURCE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select aria-label="Trust" value={trust} onChange={e => setTrust(e.target.value as Trust)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          <option value="trusted">Trusted</option>
          <option value="weak">Weak</option>
          <option value="blocked">Blocked</option>
        </select>
        <Button size="sm" disabled={!name.trim() || add.isPending} data-testid="tt-source-add" onClick={() => add.mutate()}>Add source</Button>
      </div>
      {add.isError ? <p role="alert" className="mt-1 text-sm text-red-700">{add.error.message}</p> : null}
      {update.isError ? <p role="alert" className="mt-1 text-sm text-red-700">{update.error.message}</p> : null}
    </CardBody></Card>
  );
}
